<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border">
    <div class="border-solid border-b border-0 border-gray-border px-8 pb-8">
      <div class="mt-8 inline-block pr-14 relative">
        <h2 class="font-semibold">{{ formatIntegrationType(integration.type) }}</h2>
        <side-list-menu :items="menuItems"
                        class="absolute top-0 right-0 -mt-1"
                        @delete="$emit('request-delete', integration)"
        />
      </div>
    </div>

    <form @submit.prevent="saveIntegration" class="h-full flex flex-col justify-between">
      <div class="flex flex-col gap-4 mt-8 px-8 overflow-visible pb-4">
        <div>
          <base-label id="integrationType" label="Type"/>
          <base-input id="integrationType"
                      name="integrationType"
                      variant="editor"
                      type="text"
                      class="w-full border-gray py-px mt-1 h-10"
                      :value="formatIntegrationType(integration.type)"
                      :disabled="true"
          />
        </div>

        <email-helpdesk-integration-form v-if="isKnownIntegrationType && integration.type === INTEGRATION_TYPES.EMAIL_HELPDESK"
                                         :form-data="editedIntegration"
                                         :selected-bot-id="selectedBotId"
                                         :field-errors="fieldErrors"
                                         :timezone-options="timezoneOptions"
                                         :bot-options="botOptions"
                                         :loaders="loaders"
                                         :is-edit-mode="true"
                                         @update:email="editedIntegration.email = $event"
                                         @update:subject="editedIntegration.subject = $event"
                                         @update:format="editedIntegration.format = $event"
                                         @update:sendgridTemplateId="editedIntegration.sendgrid_template_id = $event"
                                         @update:timezone="editedIntegration.timezone = $event"
                                         @update:botId="handleBotChange"
        />

        <daktela-chat-integration-form v-else-if="isKnownIntegrationType && integration.type === INTEGRATION_TYPES.DAKTELA"
                                       :form-data="editedIntegration"
                                       :selected-bot-id="selectedBotId"
                                       :field-errors="fieldErrors"
                                       :bot-options="botOptions"
                                       :loaders="loaders"
                                       :is-edit-mode="true"
                                       @update:daktelaUrl="editedIntegration.daktela_url = $event"
                                       @update:accessToken="editedIntegration.access_token = $event"
                                       @update:refreshChatPeriodMs="editedIntegration.refresh_chat_period_ms = $event"
                                       @update:refreshChatRepeatCount="editedIntegration.refresh_chat_repeat_count = $event"
                                       @update:refreshChatType="editedIntegration.refresh_chat_type = $event"
                                       @update:botId="handleBotChange"
        />

        <unknown-integration-view v-else
                                  :integration="integration"
        />

        <div v-if="error" class="text-red-500 text-sm">
          {{ error }}
        </div>
      </div>

      <busy-button v-if="isKnownIntegrationType"
                   id="saveButton"
                   variant="primary"
                   class="w-1/2 mb-10 mx-8"
                   :is-loading="isLoading"
                   :is-disabled="isLoading"
                   @click="saveIntegration"
      >
        <span class="w-full">Save Changes</span>
      </busy-button>

    </form>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import SideListMenu from '@/components/common/SideListMenu.vue'
import EmailHelpdeskIntegrationForm from '@/components/settings/integrations/EmailHelpdeskIntegrationForm.vue'
import DaktelaChatIntegrationForm from '@/components/settings/integrations/DaktelaChatIntegrationForm.vue'
import UnknownIntegrationView from '@/components/settings/integrations/UnknownIntegrationView.vue'
import { INTEGRATION_TYPES, SENDGRID_TEMPLATES, EMAIL_FORMATS, DAKTELA_REFRESH_CHAT_DEFAULTS } from '@/utils/integration.constants'
import { MENU_ITEMS } from '@/utils/editor.constants'
import { formatIntegrationType, normalizeDaktelaUrl } from '@/utils/integration.utils'
import integrationService from '@/services/amio/integration.service'
import toaster from '@/utils/toaster'
import clone from 'ramda/src/clone'
import integrationFormMixin from '@/utils/integration-form.mixin'

export default {
  name: 'SettingsIntegrationEditor',
  components: { BusyButton, SideListMenu, EmailHelpdeskIntegrationForm, DaktelaChatIntegrationForm, UnknownIntegrationView },
  mixins: [integrationFormMixin],

  props: {
    integration: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      INTEGRATION_TYPES,
      editedIntegration: {},
      selectedBotId: '',
      isLoading: false,
      error: null
    }
  },

  computed: {
    isKnownIntegrationType() {
      return this.integration.type === INTEGRATION_TYPES.EMAIL_HELPDESK ||
        this.integration.type === INTEGRATION_TYPES.DAKTELA
    },

    menuItems() {
      return [MENU_ITEMS.delete]
    }
  },

  watch: {
    integration: {
      immediate: true,
      handler(newIntegration) {
        this.editedIntegration = clone(newIntegration)

        if (newIntegration.type === INTEGRATION_TYPES.EMAIL_HELPDESK && !this.editedIntegration.format) {
          this.editedIntegration.format = EMAIL_FORMATS.HTML
        }

        if (newIntegration.type === INTEGRATION_TYPES.DAKTELA) {
          if (this.editedIntegration.daktela_url == null) {
            this.editedIntegration.daktela_url = ''
          }
          if (this.editedIntegration.access_token == null) {
            this.editedIntegration.access_token = ''
          }
          if (this.editedIntegration.refresh_chat_period_ms == null) {
            this.editedIntegration.refresh_chat_period_ms = DAKTELA_REFRESH_CHAT_DEFAULTS.refresh_chat_period_ms
          }
          if (this.editedIntegration.refresh_chat_repeat_count == null) {
            this.editedIntegration.refresh_chat_repeat_count = DAKTELA_REFRESH_CHAT_DEFAULTS.refresh_chat_repeat_count
          }
          if (!this.editedIntegration.refresh_chat_type) {
            this.editedIntegration.refresh_chat_type = DAKTELA_REFRESH_CHAT_DEFAULTS.refresh_chat_type
          }
        }

        // Set the selected bot from the integration
        if (newIntegration.bot_id) {
          this.selectedBotId = String(newIntegration.bot_id)
        } else {
          this.selectedBotId = ''
        }
      }
    },

    'editedIntegration.format'(newFormat) {
      if (this.editedIntegration.type !== INTEGRATION_TYPES.EMAIL_HELPDESK) return
      if (newFormat === EMAIL_FORMATS.TEXT) {
        this.editedIntegration.sendgrid_template_id = SENDGRID_TEMPLATES.TEXT
      } else if (newFormat === EMAIL_FORMATS.HTML) {
        this.editedIntegration.sendgrid_template_id = SENDGRID_TEMPLATES.HTML
      }
    }
  },

  methods: {
    formatIntegrationType,

    buildUpdateData() {
      if (this.integration.type === INTEGRATION_TYPES.DAKTELA) {
        this.editedIntegration.daktela_url = normalizeDaktelaUrl(this.editedIntegration.daktela_url)
        return {
          daktela_url: this.editedIntegration.daktela_url,
          access_token: this.editedIntegration.access_token,
          refresh_chat_period_ms: Number(this.editedIntegration.refresh_chat_period_ms),
          refresh_chat_repeat_count: Number(this.editedIntegration.refresh_chat_repeat_count),
          refresh_chat_type: this.editedIntegration.refresh_chat_type
        }
      }

      return {
        email: this.editedIntegration.email,
        subject: this.editedIntegration.subject,
        format: this.editedIntegration.format,
        sendgrid_template_id: this.editedIntegration.sendgrid_template_id,
        timezone: this.editedIntegration.timezone
      }
    },

    async saveIntegration() {
      this.isLoading = true
      this.error = null
      this.fieldErrors = {}

      try {
        const updateData = this.buildUpdateData()

        if (!this.selectedBotId) {
          throw new Error('Bot selection is required')
        }
        const updatedIntegration = await integrationService.updateIntegration(this.selectedBotId, this.integration.id, updateData)
        this.$emit('update-integration', updatedIntegration)
        toaster.success('Integration updated successfully')
      } catch (err) {
        this.handleApiError(err)
      } finally {
        this.isLoading = false
      }
    }
  },

  async mounted() {
    await this.loadBots()
  }
}
</script>

